import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Input,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Image,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { API_ENDPOINTS } from "../api/api.js";
import { v4 as uuidv4 } from "uuid";

/* ------------------------------
   Helpers: UTM/session/device/etc.
   ------------------------------ */
const STORAGE_KEYS = {
  SESSION: "vh_session_id",
  PERSIST_UTM: "vh_utms",
};

function readUtmFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "gclid",
      "fbclid",
      "referral_code",
    ].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });
    return utm;
  } catch (e) {
    return {};
  }
}

function persistUtms() {
  try {
    const utm = readUtmFromUrl();
    if (Object.keys(utm).length) {
      const existing = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.PERSIST_UTM) || "{}"
      );
      localStorage.setItem(
        STORAGE_KEYS.PERSIST_UTM,
        JSON.stringify({ ...existing, ...utm })
      );
    }
  } catch {}
}

function getPersistedUtms() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.PERSIST_UTM) || "{}");
  } catch {
    return {};
  }
}

function getSessionId() {
  try {
    let s = localStorage.getItem(STORAGE_KEYS.SESSION);
    if (!s) {
      s = uuidv4();
      localStorage.setItem(STORAGE_KEYS.SESSION, s);
    }
    return s;
  } catch {
    return uuidv4();
  }
}

function deviceTypeFromUA(
  ua = (typeof navigator !== "undefined" && navigator.userAgent) || ""
) {
  const uaL = ua.toLowerCase();
  if (/mobile|iphone|ipod|android|blackberry|iemobile|opera mini/.test(uaL))
    return "mobile";
  if (/tablet|ipad|playbook/.test(uaL)) return "tablet";
  return "desktop";
}

async function getGaClientId() {
  try {
    if (
      typeof window !== "undefined" &&
      window.ga &&
      typeof window.ga.getAll === "function"
    ) {
      const trackers = window.ga.getAll();
      if (trackers && trackers.length) {
        const tid = trackers[0].get("clientId");
        if (tid) return tid;
      }
    }
    if (typeof document !== "undefined") {
      const gaCookie = document.cookie
        .split("; ")
        .find((row) => row.startsWith("_ga="));
      if (gaCookie) {
        return decodeURIComponent(gaCookie.split("=")[1]);
      }
    }
  } catch (e) {}
  return null;
}

/* ------------------------------
   Component
   ------------------------------ */
export default function ContactSection() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") persistUtms();

    async function collect() {
      try {
        const now = new Date();
        const utms = getPersistedUtms();
        const meta = {
          utms,
          referrer:
            (typeof document !== "undefined" && document.referrer) || null,
          landing_page:
            (typeof window !== "undefined" &&
              window.location.pathname + window.location.search) ||
            null,
          page_url:
            (typeof window !== "undefined" && window.location.href) || null,
          timestamp: now.toISOString(),
          timezone:
            (typeof Intl !== "undefined" &&
              Intl.DateTimeFormat().resolvedOptions().timeZone) ||
            null,
          language:
            (typeof navigator !== "undefined" && navigator.language) || null,
          user_agent:
            (typeof navigator !== "undefined" && navigator.userAgent) || null,
          screen_width:
            (typeof window !== "undefined" && window.screen?.width) || null,
          screen_height:
            (typeof window !== "undefined" && window.screen?.height) || null,
          viewport_width:
            (typeof window !== "undefined" && window.innerWidth) || null,
          viewport_height:
            (typeof window !== "undefined" && window.innerHeight) || null,
          device_type: deviceTypeFromUA(),
          session_id: getSessionId(),
          ga_client_id: await getGaClientId(),
        };
        setMetadata(meta);
      } catch (e) {
        setMetadata({});
      }
    }

    collect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const basePayload = {
        name: formData.get("name") || null,
        email: formData.get("email") || null,
        phone: formData.get("phone") || null,
        message: formData.get("message") || null,
      };

      // Ensure metadata collected
      const meta = metadata || {};

      // Build payload that includes BOTH nested metadata (for future use)
      // and flattened top-level fields (for backwards compatibility)
      const payload = {
        ...basePayload,
        metadata: meta,
        // top-level utms (flattened)
        utms: meta.utms || {},
        referrer: meta.referrer || null,
        landing_page: meta.landing_page || null,
        page_url: meta.page_url || null,
        timestamp: meta.timestamp || null,
        timezone: meta.timezone || null,
        language: meta.language || null,
        user_agent: meta.user_agent || null,
        screen_width: meta.screen_width || null,
        screen_height: meta.screen_height || null,
        viewport_width: meta.viewport_width || null,
        viewport_height: meta.viewport_height || null,
        device_type: meta.device_type || null,
        session_id: meta.session_id || null,
        ga_client_id: meta.ga_client_id || null,
      };

      // send JSON
      const res = await axios.post(
        API_ENDPOINTS.pentagonContact.create,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      toast({
        title: "Message sent!",
        description: res.data?.message || "We’ll get back to you soon.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      e.target.reset();
    } catch (err) {
      console.error("Contact submit error:", err);
      toast({
        title: "Oops!",
        description:
          err?.response?.data?.message ||
          "Something went wrong. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box w="100%" bg="white" py={16}>
      <Flex
        maxW="1200px"
        mx="auto"
        direction={{ base: "column", md: "row" }}
        align="stretch"
      >
        {/* Left Image */}
        <Box flex="1">
          <Image
            src="/ContacUs.png" // replace with your image
            alt="Modern Home"
            w="100%"
            h="100%"
            objectFit="cover"
          />
        </Box>

        {/* Right Contact Form */}
        <Box
          flex="1"
          bg="#0B1D39"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={{ base: 6, md: 12 }}
          py={12}
        >
          <Box
            bg="white"
            borderRadius="md"
            p={8}
            w="100%"
            maxW="450px"
            boxShadow="lg"
          >
            <Heading size="lg" mb={6} textAlign="left">
              Contact us
            </Heading>

            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontWeight="medium" color="gray.700">
                    Full Name
                  </FormLabel>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Enter Full Name"
                    focusBorderColor="#0B1D39"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="medium" color="gray.700">
                    Contact Number
                  </FormLabel>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Your Number"
                    focusBorderColor="#0B1D39"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="medium" color="gray.700">
                    Email
                  </FormLabel>
                  <Input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    focusBorderColor="#0B1D39"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="medium" color="gray.700">
                    Message
                  </FormLabel>
                  <Textarea
                    name="message"
                    placeholder="Type your message..."
                    focusBorderColor="#0B1D39"
                  />
                </FormControl>

                <Button
                  type="submit"
                  bg="#0B1D39"
                  color="white"
                  size="md"
                  borderRadius="md"
                  _hover={{ bg: "#C9A14A", color: "#0B1D39" }}
                  isLoading={loading}
                >
                  Contact us
                </Button>
              </VStack>
            </form>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
